import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  VStack,
  useColorModeValue,
  Heading,
  Icon,
  Flex,
  AspectRatio,
  Divider,
  Tag,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue
} from "@chakra-ui/react";
import { FaRocket, FaArrowRight, FaLightbulb, FaCalendarAlt, FaPlay } from "react-icons/fa";

const NewUserPopup = ({ isOpen, onClose, accentColor }) => {
  // Colors
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.100");
  const tipBgColor = useColorModeValue("blue.50", "blue.900");
  const cardBg = useColorModeValue("gray.50", "gray.700");
  
  // Responsive values
  const modalSize = useBreakpointValue({ base: "full", sm: "lg", md: "2xl", lg: "3xl" });
  const headingSize = useBreakpointValue({ base: "md", md: "lg" });
  const iconSize = useBreakpointValue({ base: "18px", md: "24px" });
  const bodyPadding = useBreakpointValue({ base: 4, md: 6 });
  const cardGap = useBreakpointValue({ base: 2, md: 4 });
  
  // Calculate trial end date (30 days from now)
  const today = new Date();
  const trialEnd = new Date(today);
  trialEnd.setDate(trialEnd.getDate() + 30);
  const trialEndFormatted = trialEnd.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={modalSize}
      isCentered 
      motionPreset="slideInBottom"
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(6px)" />
      <ModalContent borderRadius={{ base: "md", md: "xl" }} overflow="hidden" m={{ base: 2, md: 4 }}>
        <Box position="relative">
          <Box 
            h={{ base: "6px", md: "8px" }}
            bgGradient={`linear(to-r, ${accentColor}, blue.500, purple.600)`}
            w="100%"
          />

          <ModalHeader 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between" 
            pt={{ base: 6, md: 8 }} 
            px={{ base: 4, md: 6 }}
            flexDirection={{ base: "column", sm: "row" }}
            gap={{ base: 3, sm: 0 }}
          >
            <Flex align="center">
              <Icon as={FaRocket} mr={3} color={accentColor} boxSize={iconSize} />
              <Heading size={headingSize}>Welcome to Widget Creator!</Heading>
            </Flex>
            <Tag size={{ base: "md", md: "lg" }} variant="solid" colorScheme="teal" borderRadius="full">
              <Flex align="center" gap={2}>
                <Icon as={FaCalendarAlt} />
                <Text>30-Day Trial Activated</Text>
              </Flex>
            </Tag>
          </ModalHeader>

          <ModalBody py={{ base: 4, md: 6 }} px={bodyPadding} maxH={{ base: "70vh", md: "auto" }} overflowY="auto">
            <VStack spacing={{ base: 6, md: 8 }} align="stretch">
              <Box bg={cardBg} p={{ base: 4, md: 5 }} borderRadius="lg" boxShadow="sm">
                <Flex direction={{ base: "column", md: "row" }} align={{ base: "flex-start", md: "center" }} justify="space-between" gap={4}>
                  <Stat textAlign={{ base: "left", sm: "center", md: "left" }}>
                    <StatLabel fontSize={{ base: "sm", md: "md" }} color={textColor}>Your free trial has started</StatLabel>
                    <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color={accentColor}>30 Days</StatNumber>
                    <StatHelpText fontSize={{ base: "xs", md: "sm" }}>Expires on {trialEndFormatted}</StatHelpText>
                  </Stat>
                  <Divider orientation={{ base: "horizontal", md: "vertical" }} height={{ md: "80px" }} />
                  <Text flex="1" fontSize={{ base: "sm", md: "md" }} color={textColor}>
                    You now have full access to all features for the next 30 days. Create and deploy unlimited widgets, integrate with your website, and test the AI responses.
                  </Text>
                </Flex>
              </Box>

              <Heading size={{ base: "sm", md: "md" }} color={accentColor}>Watch Our Quick Guide</Heading>
              
              <Box borderRadius="lg" overflow="hidden" boxShadow="md" position="relative">
                <AspectRatio ratio={16/9}>
                  <iframe
                    src="https://www.youtube.com/embed/voZXA6yjv-U"
                    title="Widget Creator Guide"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ 
                      width: '100%', 
                      height: '100%',
                      borderRadius: 'lg',
                    }}
                  />
                </AspectRatio>
                
                <Box
                  position="absolute"
                  top="10px"
                  right="10px"
                  bg="blackAlpha.700"
                  color="white"
                  py={1}
                  px={3}
                  borderRadius="md"
                  fontSize={{ base: "xs", md: "sm" }}
                  display={{ base: "none", sm: "block" }}
                >
                  <Flex align="center" gap={1}>
                    <Icon as={FaPlay} />
                    <Text>Guide Video</Text>
                  </Flex>
                </Box>
              </Box>
              
              <Box bg={tipBgColor} p={{ base: 3, md: 4 }} borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                <Flex align="start">
                  <Icon as={FaLightbulb} color="blue.400" mt={1} mr={3} />
                  <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
                    <Text as="span" fontWeight="bold">Pro Tip:</Text> The video above walks you through the entire process. Follow along to create your first widget in minutes!
                  </Text>
                </Flex>
              </Box>

              <Divider />

              <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium" color={accentColor}>
                Three Steps to Success:
              </Text>
              
              <Box>
                <Flex 
                  direction={{ base: "column", md: "row" }} 
                  gap={cardGap} 
                  justify="space-between"
                  w="100%"
                >
                  <Box flex="1" minW={{ base: "100%", md: "220px" }} bg={cardBg} p={{ base: 3, md: 4 }} borderRadius="lg" borderTop="4px solid" borderTopColor="teal.400" mb={{ base: 3, md: 0 }}>
                    <Flex direction="column" align="flex-start" gap={2}>
                      <Flex align="center" mb={2}>
                        <Box bg="teal.400" borderRadius="full" w={{ base: "24px", md: "28px" }} h={{ base: "24px", md: "28px" }} display="flex" alignItems="center" justifyContent="center" mr={3}>
                          <Text color="white" fontWeight="bold">1</Text>
                        </Box>
                        <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>Add Website Data</Text>
                      </Flex>
                      <Text fontSize={{ base: "xs", md: "sm" }}>Make your widget knowledgeable about your business</Text>
                    </Flex>
                  </Box>
                  
                  <Box flex="1" minW={{ base: "100%", md: "220px" }} bg={cardBg} p={{ base: 3, md: 4 }} borderRadius="lg" borderTop="4px solid" borderTopColor="blue.400" mb={{ base: 3, md: 0 }}>
                    <Flex direction="column" align="flex-start" gap={2}>
                      <Flex align="center" mb={2}>
                        <Box bg="blue.400" borderRadius="full" w={{ base: "24px", md: "28px" }} h={{ base: "24px", md: "28px" }} display="flex" alignItems="center" justifyContent="center" mr={3}>
                          <Text color="white" fontWeight="bold">2</Text>
                        </Box>
                        <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>Customize Appearance</Text>
                      </Flex>
                      <Text fontSize={{ base: "xs", md: "sm" }}>Match your brand's colors and style</Text>
                    </Flex>
                  </Box>
                  
                  <Box flex="1" minW={{ base: "100%", md: "220px" }} bg={cardBg} p={{ base: 3, md: 4 }} borderRadius="lg" borderTop="4px solid" borderTopColor="purple.500">
                    <Flex direction="column" align="flex-start" gap={2}>
                      <Flex align="center" mb={2}>
                        <Box bg="purple.500" borderRadius="full" w={{ base: "24px", md: "28px" }} h={{ base: "24px", md: "28px" }} display="flex" alignItems="center" justifyContent="center" mr={3}>
                          <Text color="white" fontWeight="bold">3</Text>
                        </Box>
                        <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>Deploy Widget</Text>
                      </Flex>
                      <Text fontSize={{ base: "xs", md: "sm" }}>Get the code and add it to your website</Text>
                    </Flex>
                  </Box>
                </Flex>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter 
            borderTop="1px solid" 
            borderTopColor="gray.100" 
            _dark={{ borderTopColor: "gray.600" }} 
            p={{ base: 4, md: 5 }}
            bg={useColorModeValue("gray.50", "gray.700")}
          >
            <Button 
              rightIcon={<FaArrowRight />} 
              colorScheme="teal" 
              size={{ base: "md", md: "lg" }} 
              onClick={onClose}
              w="full"
              h={{ base: "48px", md: "56px" }}
              bgGradient={`linear(to-r, ${accentColor}, teal.500)`}
              _hover={{
                bgGradient: `linear(to-r, ${accentColor}, teal.600)`,
                transform: "translateY(-2px)",
                boxShadow: "lg"
              }}
              transition="all 0.2s"
            >
              Let's Get Started
            </Button>
          </ModalFooter>
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default NewUserPopup;